import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type { Event } from 'hb-react/shared/apps/Events/types';

interface EventContextType {
  event: Event;
  updateEvent: Dispatch<SetStateAction<Event>>;
  eventDetailsPageLink: string;
  eventRegistrationPageLink: string;
  config: Record<string, unknown>;
}

const EventContext = createContext({} as EventContextType);

export default EventContext;

export const useEventContext = () => useContext<EventContextType>(EventContext);

export const withEventContext = (Component) => (props) => {
  const contextValue = useEventContext();

  return <Component {...props} {...contextValue} />;
};

const getEventDetailsPageLink = (
  isLoggedIn,
  eventId,
  topicId,
  isWhitelabelGroup,
) => {
  if (topicId && !isWhitelabelGroup) {
    return Routes.topic_event_path(topicId, eventId);
  }

  if (!isLoggedIn) {
    return Routes.network_event_path(eventId);
  }

  return Routes.network_events_event_path(eventId);
};

const getEventRegistrationPageLink = (
  isLoggedIn,
  eventId,
  topicId,
  isWhitelabelGroup,
) => {
  if (topicId && !isWhitelabelGroup) {
    return Routes.topic_event_registration_path(topicId, eventId);
  }

  if (!isLoggedIn) {
    return Routes.network_event_registration_path(eventId);
  }

  return Routes.network_events_event_registration_path(eventId);
};

export const EventProvider = ({
  children,
  event,
  config = {},
}: {
  children: ReactNode;
  event: Event;
  config?: Record<string, unknown>;
}) => {
  const { currentUser } = useDefaultAppContexts();
  const topicContext = useTopicContext();

  const [eventState, setEventState] = useState(event);

  useEffect(() => setEventState(event), [event]);

  const isLoggedIn = !!currentUser?.id;

  const providerState = useMemo(
    () => ({
      event: eventState,
      updateEvent: setEventState,
      eventDetailsPageLink: getEventDetailsPageLink(
        isLoggedIn,
        event.id,
        topicContext?.topicId,
        !!topicContext?.whitelabelURL,
      ),
      eventRegistrationPageLink: getEventRegistrationPageLink(
        isLoggedIn,
        event.id,
        topicContext?.topicId,
        !!topicContext?.whitelabelURL,
      ),
      config,
    }),
    [eventState, isLoggedIn, topicContext, event.id, config],
  );

  return (
    <EventContext.Provider value={providerState}>
      {children}
    </EventContext.Provider>
  );
};
